import type { Observation, Questionnaire } from '@medplum/fhirtypes';
import { auditCQuestionnaire } from './auditC';
import { gad7Questionnaire } from './gad7';
import { hoosJrQuestionnaire } from './hoosJr';
import { ipssQuestionnaire } from './ipss';
import { moodScaleQuestionnaire } from './moodScale';
import { painIntensityNrsQuestionnaire } from './painIntensityNrs';
import { pgicQuestionnaire } from './pgic';
import { phq2Questionnaire } from './phq2';
import { phq9Questionnaire } from './phq9';

/**
 * Questionnaires whose total score is tracked as a care-plan result. Order drives the result
 * picker's "Surveys" group. Adding a survey is just: define its Questionnaire (total-score code on
 * the contained `Observation` template, a short `title`) and add it here — every value below is
 * derived from the resource, so there's no parallel mapping to maintain. The result code may live on
 * any system: a real total-score LOINC (PHQ-9, GAD-7) or a minted `survey-scores` code for
 * instruments without one (Mood). See the survey FHIR design-decision doc.
 */
const SURVEY_QUESTIONNAIRES: Questionnaire[] = [
  phq9Questionnaire,
  phq2Questionnaire,
  gad7Questionnaire,
  auditCQuestionnaire,
  ipssQuestionnaire,
  pgicQuestionnaire,
  moodScaleQuestionnaire,
  painIntensityNrsQuestionnaire,
  hoosJrQuestionnaire,
];

export interface SurveyConfig {
  /** System of the result code — `http://loinc.org` or a minted `survey-scores` system. */
  resultSystem: string;
  /** Total-score code — the result the card tracks (`Goal.target.measure`, `Task.code`, picker). */
  resultCode: string;
  /** Label shown in the result picker — the Questionnaire's (short) `title`. */
  display: string;
  /** Canonical URL of the Questionnaire, pinned to its version (`url|version`). */
  questionnaireUrl: string;
}

/**
 * Derives a {@link SurveyConfig} from a survey Questionnaire: the result (total-score) coding from
 * the contained `Observation` template (regardless of system), the picker label from the (short)
 * `title`, and a version-pinned canonical URL.
 */
function toSurveyConfig(questionnaire: Questionnaire): SurveyConfig {
  const scoreObservation = questionnaire.contained?.find((r): r is Observation => r.resourceType === 'Observation');
  const resultCoding = scoreObservation?.code?.coding?.[0];
  const resultSystem = resultCoding?.system;
  const resultCode = resultCoding?.code;
  const display = questionnaire.title;
  if (!questionnaire.url || !questionnaire.version || !display || !resultSystem || !resultCode) {
    throw new Error(
      `Survey Questionnaire ${questionnaire.url ?? '(missing url)'} must declare a versioned url, a title, and a contained Observation template with a coded (system + code) result`,
    );
  }
  return { resultSystem, resultCode, display, questionnaireUrl: `${questionnaire.url}|${questionnaire.version}` };
}

/**
 * Curated surveys offered as care-plan results, derived from {@link SURVEY_QUESTIONNAIRES}. The
 * total-score code drives the result Task/Goal (like a lab); `questionnaireUrl` is written
 * (version-pinned) to `Task.input[questionnaire]`. See the survey FHIR design decision doc.
 */
export const SURVEY_CONFIGS: SurveyConfig[] = SURVEY_QUESTIONNAIRES.map(toSurveyConfig);
